/**
 * @class CartDrawerUpsell
 * @extends HTMLElement
 *
 * @description
 * A custom Web Component (<cart-drawer-upsell>) for managing upsell products
 * inside the cart drawer. Handles variant selection, product image updates,
 * add-to-cart actions, and initializes a Flickity slider for multiple upsells.
 *
 * @example
 * <cart-drawer-upsell>
 *   <div class="complementary__products">
 *     <div class="product__complementary-item">
 *       <script type="application/json">
 *         {{ product.variants | json }}
 *       </script>
 *       <!-- swatches, image, and add-to-cart form markup here -->
 *     </div>
 *   </div>
 * </cart-drawer-upsell>
 *
 * @features
 * - Collects upsell product items on initialization
 * - Removes itself if no upsell products exist
 * - Initializes a Flickity slider for multiple upsells
 * - Handles variant option changes:
 *   - Updates variant IDs for add-to-cart forms
 *   - Updates product images and labels
 *   - Enables/disables Add to Cart button based on availability
 * - Handles upsell add-to-cart actions via AJAX
 *   - Posts selected variant to Shopify’s `/cart/add.js`
 *   - Triggers mini-cart drawer refresh after adding
 *
 * @usage
 * - Wrap upsell products inside <cart-drawer-upsell>
 * - Each product requires:
 *   - Variant JSON data in a <script type="application/json">
 *   - Variant swatch inputs (.swatch-element input)
 *   - Add to Cart button with id="addToCart"
 *   - Hidden input for variant ID (.formVariantId)
 *   - Product image container (.js-product-image img)
 * - Requires Flickity library for slider functionality
 */

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define(
    'cart-drawer-upsell',

    class CartDrawerUpsell extends HTMLElement {
      constructor() {
        super();

        this.upsellProducts = Array.from(this.querySelectorAll(".product__complementary-item"));
    
        if (this.upsellProducts.length == 0) {
          this.remove();
          return;
        }
        
        this.initSlider();
    
        this.upsellProducts.forEach((product) =>
          this.handleUpsellVariantChange(product)
        );
    
        this.upsellAddToCartButtonsArray = Array.from(
          this.querySelectorAll(".cart-upsell__add-to-cart")
        );
    
        this.upsellAddToCartButtonsArray.forEach((button) =>
          button.addEventListener("click", this.handleUpsellAddToCart.bind(this))
        );
      }

      initSlider() {
        if (this.upsellProducts.length > 1) {
          const slider = this.querySelector(".complementary__products");

          if (!slider) return;

          const flickityData = {
            fade: false,
            wrapAround: false,
            cellAlign: "left",
            draggable: true,
            contain: true,
            imagesLoaded: true,
            lazyLoad: 4,
            pageDots: false,
            prevNextButtons: true,
            selectedAttraction: 0.2,
            friction: 0.8,
            dragThreshold: 15,
            on: {
              dragStart: function () {
                document.ontouchmove = (e) => e.preventDefault();
              },
              dragEnd: function () {
                document.ontouchmove = () => true;
              },
            },
          };

          new Flickity(slider, flickityData);
        }
      }
    
      handleUpsellVariantChange(product) {
        const productData = JSON.parse(product.querySelector("[type='application/json']").textContent);
        const productVariantSelectors = product.querySelectorAll(".swatch-element input");
  
        productVariantSelectors.forEach((input) => {
          input.addEventListener("change", (event) => {
            event.preventDefault();
  
            const selectedOptions = Array.from(product.querySelectorAll(".product__variants-swatches input:checked")).map((item) => item.value);
            const selectedVariant = productData.find((item) => selectedOptions.every((option, index) => option === item.options[index])) || null;
            const addToCart = product.querySelector("#addToCart");
  
            if (!selectedVariant) {
              addToCart.setAttribute("disabled", true);
              addToCart.value = "Unavailable";
              return;
            } else {
              addToCart.removeAttribute("disabled");
              addToCart.value = "Add To Cart";
            }
  
            const optionLabels = product.querySelectorAll(".complementary-variant-label");
            optionLabels.forEach((label, index) => {
              label.textContent = selectedVariant.options[index];
            });
  
            const variantImage = product.querySelector(".js-product-image img");
            variantImage.setAttribute("src","https:" + selectedVariant.featured_image.src + "&width=280");
            variantImage.removeAttribute("srcset");
  
            if (selectedVariant.available) {
              const variantFormId = product.querySelector(".formVariantId");
              variantFormId.value = selectedVariant.id;
  
              addToCart.removeAttribute("disabled");
              addToCart.value = "Add to Cart";
            } else {
              addToCart.setAttribute("disabled", true);
              addToCart.value = "Sold Out";
            }
          });
        });
      }
    
      handleUpsellAddToCart(event) {
        event.preventDefault();
        const productId = event.target.parentElement.querySelector(".formVariantId").value;
        
        const formData = {
          items: [
            {
              id: productId,
              quantity: 1,
            },
          ],
        };
    
        fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        })
        .then((response) => response.json())
        .then(function (data) {
          if (data.status === 422) {
            console.log("error");
          } else {
            // Shopify.theme.ajaxCart.init();
            document.querySelector(".js-mini-cart-trigger").click();
          }
        })
        .catch((error) => {
          console.error("Fetch error:", error);
        });
      }
    }
  )
}

/**
 * @class ProductVideoLoader
 * @extends HTMLElement
 *
 * @description
 * A custom Web Component for lazy-loading product videos.
 * 
 * Instead of loading `<video>` tags on page render, this component 
 * injects the `<video>` element dynamically when the user clicks 
 * a trigger button. This improves page performance by avoiding 
 * unnecessary media requests on initial load.
 *
 * @example
 * <product-video-loader>
 *   <div class="video-container">
 *     <button class="js-load-video" 
 *             data-video-src="{{ product.metafields.custom.product_video | file_url }}">
 *       ▶ Watch Video
 *     </button>
 *     <div class="video-wrapper"></div>
 *   </div>
 * </product-video-loader>
 *
 * @usage
 * - Place a `<product-video-loader>` wrapper around any video container.
 * - Add a trigger button with `.js-load-video` and a `data-video-src` attribute.
 * - Include an empty `.video-wrapper` where the video will be injected.
 *
 * @features
 * - Removes component if no trigger buttons are found.
 * - Creates and injects `<video>` + `<source>` dynamically on user click.
 * - Autoplays the video after injection.
 * - Works with multiple video containers per component instance.
 */

if (!customElements.get('product-video-loader')) {
  customElements.define(
    'product-video-loader',

    class ProductVideoLoader extends HTMLElement {
      constructor() {
        super();

        this.loadButtons = Array.from(
          this.querySelectorAll(".js-load-video")
        );

        if (this.loadButtons.length === 0) {
          this.remove();
          return;
        }

        this.loadButtons.forEach((button) => {
          button.addEventListener("click", this.handleVideoLoad.bind(this));
        });
      }

      handleVideoLoad(event) {
        event.preventDefault();

        const button = event.currentTarget;
        const videoSrc = button.getAttribute("data-video-src");
        const container = button.closest(".video-container");
        const wrapper = container.querySelector(".video-wrapper");

        if (!videoSrc || !wrapper) return;

        if (wrapper.querySelector("video")) return;

        const video = document.createElement("video");
        video.setAttribute("controls", true);
        video.setAttribute("playsinline", true);
        video.setAttribute("muted", true);
        video.setAttribute("loop", true);
        video.setAttribute("preload", "none");

        const source = document.createElement("source");
        source.setAttribute("src", videoSrc);
        source.setAttribute("type", "video/mp4");

        video.appendChild(source);

        const closeBtn = document.createElement("span");
        closeBtn.className = "video-close";
        closeBtn.setAttribute("aria-label", "Close video");
        closeBtn.setAttribute("role", "button");
        closeBtn.innerHTML = "x";

        closeBtn.addEventListener("click", () => {
          wrapper.innerHTML = "";
          button.removeAttribute("disabled");
        });

        wrapper.innerHTML = "";
        wrapper.appendChild(video);
        wrapper.appendChild(closeBtn);

        button.setAttribute("disabled", true);

        video.play();
      }
    }
  )
}
